import { useClickOutside } from '@mnd-frontend/hooks';
import { Button, Icons } from '@mnd-frontend/ui/publish/components';
import { hideScrollbar } from '@mnd-frontend/ui/publish/utils';
import { NEUTRAL_WHITE, SPACING_1, SPACING_2 } from '@mnd-frontend/ui/shared/tokens';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import mndUrl, { SubDomain } from '../../../../../lib/mndUrl';
import routes from '../../../../../routes';
import DebugInfo from '../../DebugInfo';
import { type Item, type MainMenuItem, MenuState, type Pressroom } from '../types';
import { getMatchingRoutes } from '../utils';
import LogoOrPressroomSelector from './LogoOrPressroomSelector';
import MobileNavigationItem from './MobileNavigationItem';
import TrialMenuItem from './TrialMenuItem';

const MobileNav = styled.nav<{ $show: boolean }>`
  display: ${({ $show }) => ($show ? 'grid' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  max-width: 375px;
  box-shadow: rgba(0, 0, 0, 0.56) 0px 22px 70px 4px;
  z-index: 101;
  background-color: ${NEUTRAL_WHITE};
  grid-template-rows: auto 1fr;
  height: 100vh;
  & > button {
    justify-self: flex-end;
    margin-right: 1rem;
  }
`;

const MobileNavList = styled.ul<{ $windowHeight: number | null }>`
  list-style-type: none;
  padding: 0 0 4rem 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1};
  overflow-y: scroll;
  height: ${({ $windowHeight }) => ($windowHeight ? `${$windowHeight}px` : '100vh')};
  ${hideScrollbar};
`;

const Wrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: ${SPACING_2};
`;

interface MobileNavigationProps {
  navigationItems: MainMenuItem[];
  isMynewsdeskAdmin: boolean;
  renderPressroomSelector: boolean;
  trial: boolean;
  newsroomLogo?: string | null;
  siteCode?: string;
  isSubNavOpen: MenuState | null;
  pressrooms: Pressroom[];
  isOnlyJournalist: boolean;
  journalistItems: Item[];
  topGroup: MenuState[];
  setIsSubNavOpen: (val: MenuState | null) => void;
  pressroomName: string;
}
const MobileNavigation = ({
  navigationItems,
  isMynewsdeskAdmin,
  renderPressroomSelector,
  trial,
  newsroomLogo,
  siteCode,
  isSubNavOpen,
  pressrooms,
  isOnlyJournalist,
  journalistItems,
  topGroup,
  setIsSubNavOpen,
  pressroomName,
}: MobileNavigationProps) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [windowHeight, setWindowHeight] = useState<number | null>(null);
  const mobileNavRef = useRef<HTMLDivElement>(null);

  useClickOutside(
    mobileNavRef,
    () => {
      if (showMenu) {
        setShowMenu(false);
      }
    },
    showMenu,
  );

  useEffect(() => {
    if (showMenu) {
      document.body.classList.add('block-scrolling');
    } else if (!showMenu) {
      document.body.classList.remove('block-scrolling');
    } else {
      document.body.classList.remove('block-scrolling');
    }
  }, [showMenu]);

  useEffect(() => {
    if (!showMenu) return;
    const resizeObserver = new ResizeObserver(() => {
      setWindowHeight(window.innerHeight);
    });
    resizeObserver.observe(document.body);
    return () => resizeObserver.disconnect();
  }, [showMenu]);

  return (
    <>
      <LogoOrPressroomSelector
        loading={false}
        newsroomLogo={newsroomLogo}
        pressroomName={pressroomName}
        pressroomSelector={renderPressroomSelector}
        itemData={{
          heading: 'Select newsroom',
          menuState: MenuState.PressroomSelector,
          newsroomLogo,
          items:
            pressrooms.map(p => {
              return {
                text: p.name,
                url: mndUrl(SubDomain.Publish, routes.dashboard({ pressroomId: p.id })),
                siteCode: p.siteCode,
              };
            }) ?? [],
        }}
        siteCode={siteCode}
        isSubNavOpen={isSubNavOpen}
        open={() => setIsSubNavOpen(MenuState.PressroomSelector)}
        close={() => setIsSubNavOpen(null)}
      />
      {trial && (
        <TrialMenuItem
          isOpen={isSubNavOpen === MenuState.Trial}
          close={() => setIsSubNavOpen(null)}
          open={() => setIsSubNavOpen(MenuState.Trial)}
        />
      )}
      <Wrapper>
        {isMynewsdeskAdmin && <DebugInfo fixedPosition={false} />}
        <Button
          type="button"
          configuration="text"
          onClick={() => {
            setWindowHeight(height => (height ? height : window.innerHeight));
            setShowMenu(prev => !prev);
          }}
          icon={<Icons.Menu $size="xs" />}
          aria-label="Toggle main navigation menu"
        />
      </Wrapper>
      <MobileNav ref={mobileNavRef} $show={showMenu} aria-label={t('aria_label_main_navigation')}>
        <Button
          type="button"
          configuration="text"
          icon={<Icons.Close />}
          onClick={() => setShowMenu(false)}
          aria-label="Close menu"
        />
        <MobileNavList $windowHeight={windowHeight}>
          {navigationItems.map(navItem => (
            <MobileNavigationItem
              key={`mobile-${navItem.menuState}`}
              itemData={{
                ...navItem,
                matchingRoutes: getMatchingRoutes(navItem.items),
              }}
              topGroup={topGroup}
              close={() => setShowMenu(false)}
            />
          ))}
          {isOnlyJournalist && (
            <MobileNavigationItem
              itemData={{
                heading: 'Journalist',
                menuState: MenuState.User,
                icon: <Icons.User />,
                items: journalistItems,
              }}
              topGroup={topGroup}
              close={() => setShowMenu(false)}
            />
          )}
        </MobileNavList>
      </MobileNav>
    </>
  );
};

export default MobileNavigation;
